import { CartFragment } from '@emico-hooks/cart-fragment'
import { Trans } from '@lingui/macro'
import cx from 'classnames'
import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'

import styles from './FreeGiftsConfirmModal.module.scss'
import Button from '../../input/Button'
import Loader from '../../presentation/Loader'
import ResponsiveModal from '../../presentation/ResponsiveModal/ResponsiveModal'
import Heading from '../../typography/Heading'
import CartGiftItem from '../CartGiftItem'
import { useCartItems } from '../CartPage/useCartItems'

export type FreeGiftModalHandle = {
    open: (
        productIds: number[],
        onConfirm: () => Promise<CartFragment | undefined>,
    ) => Promise<boolean>
}

interface Props {}

// eslint-disable-next-line react/display-name
const FreeGiftsConfirmModal = forwardRef<FreeGiftModalHandle, Props>(
    (_, ref) => {
        const [open, setOpen] = useState<boolean>()
        const [loading, setLoading] = useState<boolean>()

        const productIdsRef = useRef<number[] | null>(null)

        const onConfirmRef = useRef<{
            onConfirm: () => Promise<CartFragment | undefined | null>
        }>()
        const promiseRef = useRef<{
            resolve: (proceed: boolean) => void
        }>()

        const { freeGifts } = useCartItems()

        const getRuleByProductId = useCallback(
            (productId: number) =>
                freeGifts?.find((freeGift) =>
                    freeGift.products?.find((prod) => prod?.id === productId),
                ),
            [freeGifts],
        )

        useImperativeHandle(ref, () => ({
            open(
                productIds: number[],
                onConfirm: () => Promise<CartFragment | undefined>,
            ) {
                onConfirmRef.current = { onConfirm }
                if (productIds && productIds.length) {
                    productIdsRef.current = productIds
                    setOpen(true)
                }
                return new Promise((resolve) => {
                    promiseRef.current = { resolve }
                })
            },
        }))

        return open ? (
            <ResponsiveModal
                className={cx(styles.container, styles.scrollLock)}
                onBack={() => {
                    setOpen(false)
                    return promiseRef.current?.resolve(false)
                }}
                header={
                    <div className={styles.header}>
                        <Heading variant="h2" element="h2" color="dark">
                            <Trans id="cart.freegifts.restore.title">
                                O dear
                            </Trans>
                        </Heading>

                        <div>
                            <div className={styles.headerDescription}>
                                <Trans id="cart.freegifts.restore.description">
                                    If you remove this product, you will lose
                                    your free gift(s). Are you sure you want to
                                    proceed?
                                </Trans>
                            </div>
                        </div>
                    </div>
                }
                footer={
                    <div className={styles.actions}>
                        <Button
                            name="proceedRemove"
                            category="cart.freegifts.restore"
                            variant="primary"
                            minWidth
                            disabled={loading}
                            onClick={async () => {
                                setLoading(true)
                                await onConfirmRef.current?.onConfirm()
                                setLoading(false)
                                setOpen(false)
                                return promiseRef.current?.resolve(true)
                            }}
                        >
                            <Trans id="cart.freegifts.restore.proceed">
                                Continue
                            </Trans>
                        </Button>
                        <Button
                            name="proceedCancel"
                            category="cart.freegifts.restore.proceed"
                            variant="link"
                            minWidth
                            onClick={() => {
                                setOpen(false)
                                return promiseRef.current?.resolve(false)
                            }}
                        >
                            <Trans id="cart.freegifts.restore.cancel">
                                Cancel
                            </Trans>
                        </Button>
                    </div>
                }
            >
                <div className={styles.body}>
                    {loading ? (
                        <div className={styles.loader}>
                            <Loader />
                            <span className={styles.reason}>
                                <Trans id="cart.freegifts.restore.confirmed">
                                    Updating your shoppingcart
                                </Trans>
                            </span>
                        </div>
                    ) : (
                        productIdsRef.current?.map((prodId) => {
                            const rule = getRuleByProductId(prodId)

                            return (
                                <CartGiftItem
                                    key={prodId}
                                    productId={prodId}
                                    rule={rule}
                                    hideConfig
                                    hideProgress
                                    className={styles.center}
                                />
                            )
                        })
                    )}
                </div>
            </ResponsiveModal>
        ) : null
    },
)

export default FreeGiftsConfirmModal
